import { useUpdateCustomer } from "api/customers/service";
import React, { forwardRef, useImperativeHandle, useMemo } from "react";
import classNames from "classnames";
import { getActiveColorStyle } from "utils/colorUtils";
import {
    getTheme2FormFieldsCss,
    getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import {
    Row,
    Col,
    FormGroup,
    Label,
    Input,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import ReactDatetime from "components/CustomDatePicker";
import { formatPriceWithDollar } from "utils/currency";
import { formatDateForPicker } from "utils/dateUtils";
import { useGetCountries } from "api/countries/service";
import Select from "components/Select";
import StatesInput from "components/StatesInput";
import { formatPhoneNumber } from "utils/phoneUtils";

const CustomerDetailTab = forwardRef(({
    formData,
    handleChange,
    id,
    statesData,
    activeColor = "blue",
    isTheme2 = false,
}, ref) => {
    const { t } = useTranslation();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const selectStyles = useMemo(
        () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    const theme2FieldsCss = useMemo(
        () =>
            isTheme2
                ? getTheme2FormFieldsCss({
                    scopeSelector: ".customer-detail-tab.customer-detail-tab--theme2",
                    fieldsSelector: ".customer-detail-tab-fields",
                })
                : "",
        [isTheme2]
    );
    const inputStyle14 = isTheme2
        ? { fontSize: "14px" }
        : { borderRadius: 0, fontSize: "14px" };
    const dateInputStyle = isTheme2 ? { fontSize: "14px" } : { borderRadius: 0 };
    const updateCustomerMutation = useUpdateCustomer();
    const { data: countriesData } = useGetCountries();

    const findCountry = (value) => {
        if (!countriesData?.data) return undefined;
        return countriesData.data.find(
            (c) => String(c.id) === String(value) || c.name === value || c.documentId === value
        );
    };

    // Alt ID country/state
    const selectedAltIdCountry = useMemo(() => findCountry(formData.alt_id_country), [countriesData, formData.alt_id_country]);
    const altIdStateOptions = selectedAltIdCountry?.states || [];
    const altIdHasStates = Array.isArray(altIdStateOptions) && altIdStateOptions.length > 0;

    // Current address country/state
    const selectedCurrentAddrCountry = useMemo(() => findCountry(formData.current_address?.country), [countriesData, formData.current_address?.country]);
    const currentAddrStateOptions = selectedCurrentAddrCountry?.states || [];
    const currentAddrHasStates = Array.isArray(currentAddrStateOptions) && currentAddrStateOptions.length > 0;

    // Previous address country/state
    const selectedPrevAddrCountry = useMemo(() => findCountry(formData.previous_address?.prev_country), [countriesData, formData.previous_address?.prev_country]);
    const prevAddrStateOptions = selectedPrevAddrCountry?.states || [];
    const prevAddrHasStates = Array.isArray(prevAddrStateOptions) && prevAddrStateOptions.length > 0;

    // Current employment country/state
    const selectedEmploymentCountry = useMemo(() => findCountry(formData.current_employment?.employment_country), [countriesData, formData.current_employment?.employment_country]);
    const employmentStateOptions = selectedEmploymentCountry?.states || [];
    const employmentHasStates = Array.isArray(employmentStateOptions) && employmentStateOptions.length > 0;

    // Previous employment country/state
    const selectedPrevEmploymentCountry = useMemo(() => findCountry(formData.prev_employment_country), [countriesData, formData.prev_employment_country]);
    const prevEmploymentStateOptions = selectedPrevEmploymentCountry?.states || [];
    const prevEmploymentHasStates = Array.isArray(prevEmploymentStateOptions) && prevEmploymentStateOptions.length > 0;
    const handleSave = async () => {
        const previousEmploymentData = {
            employer: formData.prev_employment_employer,
            supervisor_phone: formData.prev_employment_supervisor_phone,
            position: formData.prev_employment_position,
            employment_type: formData.prev_employment_type,
            income: formData.prev_employment_income,
            pay_frequency: formData.prev_employment_pay_frequency,
            years_at: formData.prev_employment_years_at,
            months_at: formData.prev_employment_months_at,
            address_1: formData.prev_employment_address_1,
            address_2: formData.prev_employment_address_2,
            zip_code: formData.prev_employment_zip_code,
            city: formData.prev_employment_city,
            county: formData.prev_employment_county,
            state: formData.prev_employment_state,
            state_name: formData.prev_employment_state_name,
            country: formData.prev_employment_country,
            email: formData.prev_employment_email
        };

        // API'ye gönderilecek veriyi hazırla
        const apiData = {
            ...formData,
            previous_employment: previousEmploymentData
        };

        // Previous employment alanlarını ana formData'dan çıkar
        delete apiData.prev_employment_employer;
        delete apiData.prev_employment_supervisor_phone;
        delete apiData.prev_employment_position;
        delete apiData.prev_employment_type;
        delete apiData.prev_employment_income;
        delete apiData.prev_employment_pay_frequency;
        delete apiData.prev_employment_years_at;
        delete apiData.prev_employment_months_at;
        delete apiData.prev_employment_address_1;
        delete apiData.prev_employment_address_2;
        delete apiData.prev_employment_zip_code;
        delete apiData.prev_employment_city;
        delete apiData.prev_employment_county;
        delete apiData.prev_employment_state;
        delete apiData.prev_employment_state_name;
        delete apiData.prev_employment_country;
        delete apiData.prev_employment_email;

        updateCustomerMutation.mutate({ id: id, data: apiData });
        return { success: true };
    };

    useImperativeHandle(ref, () => ({
        handleSave
    }));

    return (
        <div className={classNames("customer-detail-tab", { "customer-detail-tab--theme2": isTheme2 })}>
            {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
            <div className={classNames({ "customer-detail-tab-fields": isTheme2 })}>
            <Row>
                <Col md="6">
                    <div className="card rounded-0 ">
                        <div className="card-header font-weight-bold">{t('customerDetailTab.identityInformation')}</div>
                        <div className="card-body pt-0">
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="full_name">{t('customerDetailTab.fullName')}</Label>
                                        <Input
                                            id="full_name"
                                            type="text"
                                            value={formData.full_name}
                                            onChange={(e) => handleChange("full_name", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterFullName')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="ssn">{t('customerDetailTab.ssn')}</Label>
                                        <Input
                                            id="ssn"
                                            type="text"
                                            value={formData.ssn}
                                            onChange={(e) => handleChange("ssn", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterSSN')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>

                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="date_of_birth">{t('customerDetailTab.dateOfBirth')}</Label>
                                        <ReactDatetime
                                            inputProps={{
                                                className: "form-control",
                                                placeholder: t('customerDetailTab.selectDate'),
                                                style: dateInputStyle
                                            }}
                                            value={formData.date_of_birth ? formatDateForPicker(formData.date_of_birth) : formData.date_of_birth}
                                            onChange={(date) => handleChange("date_of_birth", date)}
                                            dateFormat="MM-DD-YYYY"
                                            timeFormat={false}
                                            closeOnSelect={true}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="gender">{t('customerDetailTab.gender')}</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            placeholder={t('customerDetailTab.selectGender')}
                                            name="gender"
                                            isSearchable={false}
                                            closeMenuOnSelect={true}
                                            value={formData.gender ? {
                                                value: formData.gender,
                                                label: formData.gender === "Male" ? t('customerDetailTab.male') : t('customerDetailTab.female')
                                            } : null}
                                            onChange={(option) => {
                                                handleChange("gender", option?.value || "");
                                            }}
                                            options={[
                                                { value: "Male", label: t('customerDetailTab.male') },
                                                { value: "Female", label: t('customerDetailTab.female') }
                                            ]}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="12">
                                    <FormGroup check className="mt-2">
                                        <Label check>
                                            <Input
                                                style={inputStyle14}
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={!!formData.is_business}
                                                onChange={(e) => handleChange("is_business", e.target.checked)}
                                            />
                                            <span className="form-check-sign" />
                                            {t('customerDetailTab.isABusiness')}
                                        </Label>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="doing_business">{t('customerDetailTab.doingBusinessAs')}</Label>
                                        <Input
                                            id="doing_business"
                                            type="text"
                                            disabled={!formData.is_business}
                                            value={formData.doing_business}
                                            onChange={(e) => handleChange("doing_business", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterDBA')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="fed_id">{t('customerDetailTab.fedId')}</Label>
                                        <Input
                                            id="fed_id"
                                            type="text"
                                            disabled={!formData.is_business}
                                            value={formData.fed_id}
                                            onChange={(e) => handleChange("fed_id", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterFederalId')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>

                <Col md="6">
                    <div className="card rounded-0 ">
                        <div className="card-header font-weight-bold">{t('customerDetailTab.driversLicenseInformation')}</div>
                        <div className="card-body pt-0">
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="dl">{t('customerDetailTab.dl')}</Label>
                                        <Input
                                            id="dl"
                                            type="text"
                                            value={formData.dl}
                                            onChange={(e) => handleChange("dl", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterDLNumber')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="dl_date">{t('customerDetailTab.dlIssueDate')}</Label>
                                        <ReactDatetime
                                            inputProps={{
                                                className: "form-control",
                                                placeholder: t('customerDetailTab.selectDate'),
                                                style: dateInputStyle
                                            }}
                                            value={formData.dl_date ? formatDateForPicker(formData.dl_date) : formData.dl_date}
                                            onChange={(date) => handleChange("dl_date", date)}
                                            dateFormat="MM-DD-YYYY"
                                            timeFormat={false}
                                            closeOnSelect={true}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="dl_state">{t('customerDetailTab.dlState')}</Label>
                                        <StatesInput
                                            name="dl_state"
                                            value={formData.dl_state ? { id: formData.dl_state?.id } : null}
                                            onChange={(stateObject) => {
                                                handleChange("dl_state", stateObject ? { id: Number(stateObject.id) } : null);
                                            }}
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            placeholder={t('customerDetailTab.selectState')}
                                            countrySelected={true}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="dl_exp">{t('customerDetailTab.dlExpiration')}</Label>
                                        <ReactDatetime
                                            inputProps={{
                                                className: "form-control",
                                                placeholder: t('customerDetailTab.selectDate'),
                                                style: dateInputStyle
                                            }}
                                            value={formData.dl_exp ? formatDateForPicker(formData.dl_exp) : formData.dl_exp}
                                            onChange={(date) => handleChange("dl_exp", date)}
                                            dateFormat="MM-DD-YYYY"
                                            timeFormat={false}
                                            closeOnSelect={true}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="12">
                                    <FormGroup>
                                        <Label for="marital_status">{t('customerDetailTab.maritalStatus')}</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            placeholder={t('customerDetailTab.selectStatus')}
                                            name="marital_status"
                                            isSearchable={false}
                                            closeMenuOnSelect={true}
                                            value={formData.marital_status ? {
                                                value: formData.marital_status,
                                                label: formData.marital_status === "Single" ? t('customerDetailTab.single') :
                                                    formData.marital_status === "Married" ? t('customerDetailTab.married') :
                                                        formData.marital_status === "Divorced" ? t('customerDetailTab.divorced') :
                                                            formData.marital_status === "Widowed" ? t('customerDetailTab.widowed') :
                                                                formData.marital_status
                                            } : null}
                                            onChange={(option) => {
                                                handleChange("marital_status", option?.value || "");
                                            }}
                                            options={[
                                                { value: "Single", label: t('customerDetailTab.single') },
                                                { value: "Married", label: t('customerDetailTab.married') },
                                                { value: "Divorced", label: t('customerDetailTab.divorced') },
                                                { value: "Widowed", label: t('customerDetailTab.widowed') }
                                            ]}
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row className="">
                <Col md="6">
                    <div className="card rounded-0 ">
                        <div className="card-header font-weight-bold">{t('customerDetailTab.contactInformation')}</div>
                        <div className="card-body pt-0">
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="email">{t('customerDetailTab.email')}</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleChange("email", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterEmail')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="cell_phone">{t('customerDetailTab.cellPhone')}</Label>
                                        <Input
                                            id="cell_phone"
                                            type="text"
                                            value={formData.cell_phone}
                                            onChange={(e) => handleChange("cell_phone", formatPhoneNumber(e.target.value))}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterCellPhone')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="home_phone">{t('customerDetailTab.homePhone')}</Label>
                                        <Input
                                            id="home_phone"
                                            type="text"
                                            value={formData.home_phone}
                                            onChange={(e) => handleChange("home_phone", formatPhoneNumber(e.target.value))}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterHomePhone')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="home_phone_2">{t('customerDetailTab.homePhone2')}</Label>
                                        <Input
                                            id="home_phone_2"
                                            type="text"
                                            value={formData.home_phone_2}
                                            onChange={(e) => handleChange("home_phone_2", formatPhoneNumber(e.target.value))}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterHomePhone2')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="work_phone">{t('customerDetailTab.workPhone')}</Label>
                                        <Input
                                            id="work_phone"
                                            type="text"
                                            value={formData.work_phone}
                                            onChange={(e) => handleChange("work_phone", formatPhoneNumber(e.target.value))}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterWorkPhone')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="work_extension">{t('customerDetailTab.workExtension')}</Label>
                                        <Input
                                            id="work_extension"
                                            type="text"
                                            value={formData.work_extension}
                                            onChange={(e) => handleChange("work_extension", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterWorkExtension')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="work_phone_2">{t('customerDetailTab.workPhone2')}</Label>
                                        <Input
                                            id="work_phone_2"
                                            type="text"
                                            value={formData.work_phone_2}
                                            onChange={(e) => handleChange("work_phone_2", formatPhoneNumber(e.target.value))}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterWorkPhone2')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="work_extension_2">{t('customerDetailTab.workExtension2')}</Label>
                                        <Input
                                            id="work_extension_2"
                                            type="text"
                                            value={formData.work_extension_2}
                                            onChange={(e) => handleChange("work_extension_2", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterWorkExtension2')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>
                <Col md="6">
                    <div className="card rounded-0 ">
                        <div className="card-header font-weight-bold">{t('customerDetailTab.alternateIdentityInfo')}</div>
                        <div className="card-body pt-0">
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="alt_id_type">{t('customerDetailTab.alternateGovtIdType')}</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            placeholder={t('customerDetailTab.selectIdType')}
                                            name="alt_id_type"
                                            isSearchable={false}
                                            closeMenuOnSelect={true}
                                            value={formData.alt_id_type ? {
                                                value: formData.alt_id_type,
                                                label: formData.alt_id_type === "1" ? t('customerDetailTab.usPassport') :
                                                    formData.alt_id_type === "2" ? t('customerDetailTab.usStateId') :
                                                        formData.alt_id_type === "3" ? t('customerDetailTab.usMilitaryId') :
                                                            formData.alt_id_type === "4" ? t('customerDetailTab.usDhsId') :
                                                                formData.alt_id_type === "5" ? t('customerDetailTab.usCisId') :
                                                                    formData.alt_id_type === "6" ? t('customerDetailTab.usStateDeptId') :
                                                                        formData.alt_id_type === "7" ? t('customerDetailTab.photoId') :
                                                                            formData.alt_id_type === "8" ? t('customerDetailTab.matriculaConsular') :
                                                                                formData.alt_id_type === "9" ? t('customerDetailTab.foreignPassport') :
                                                                                    formData.alt_id_type === "10" ? t('customerDetailTab.natoId') :
                                                                                        formData.alt_id_type === "11" ? t('customerDetailTab.otherMilitaryStatus') :
                                                                                            formData.alt_id_type
                                            } : null}
                                            onChange={(option) => {
                                                handleChange("alt_id_type", option?.value || "");
                                            }}
                                            options={[
                                                { value: "1", label: t('customerDetailTab.usPassport') },
                                                { value: "2", label: t('customerDetailTab.usStateId') },
                                                { value: "3", label: t('customerDetailTab.usMilitaryId') },
                                                { value: "4", label: t('customerDetailTab.usDhsId') },
                                                { value: "5", label: t('customerDetailTab.usCisId') },
                                                { value: "6", label: t('customerDetailTab.usStateDeptId') },
                                                { value: "7", label: t('customerDetailTab.photoId') },
                                                { value: "8", label: t('customerDetailTab.matriculaConsular') },
                                                { value: "9", label: t('customerDetailTab.foreignPassport') },
                                                { value: "10", label: t('customerDetailTab.natoId') },
                                                { value: "11", label: t('customerDetailTab.otherMilitaryStatus') }
                                            ]}
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="alt_id_number">{t('customerDetailTab.alternateGovtIdNumber')}</Label>
                                        <Input
                                            id="alt_id_number"
                                            type="text"
                                            value={formData.alt_id_number}
                                            onChange={(e) => handleChange("alt_id_number", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterIdNumber')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="alt_id_country">{t('customerDetailTab.alternateGovtIdCountry')}</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            placeholder={t('selectCountry')}
                                            name="alt_id_country"
                                            isSearchable
                                            closeMenuOnSelect={true}
                                            value={formData.alt_id_country ? {
                                                value: formData.alt_id_country,
                                                label: countriesData?.data?.find(c => String(c.id) === String(formData.alt_id_country))?.name || formData.alt_id_country
                                            } : null}
                                            onChange={(option) => {
                                                handleChange("alt_id_country", option?.value || "");
                                                handleChange("alt_id_state", null);
                                            }}
                                            options={countriesData?.data?.map(option => ({
                                                value: String(option.id),
                                                label: option.name
                                            })) || []}
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="alt_id_state">{t('customerDetailTab.alternateGovtIdState')}</Label>
                                        <StatesInput
                                            name="alt_id_state"
                                            value={formData.alt_id_state ? { id: formData.alt_id_state?.id } : null}
                                            onChange={(stateObject) => {
                                                handleChange("alt_id_state", stateObject ? { id: Number(stateObject.id) } : null);
                                            }}
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            isSearchable
                                            isClearable
                                            placeholder={!formData.alt_id_country ? t('selectCountryFirst') :
                                                !altIdHasStates ? t('noStatesForCountry') : t('selectState')}
                                            countrySelected={!!formData.alt_id_country}
                                            isDisabled={!altIdHasStates}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row className="">
                <Col md="6">
                    <div className="card rounded-0 ">
                        <div className="card-header font-weight-bold">Current Address</div>
                        <div className="card-body pt-0">
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="country">{t('country')}</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            placeholder={t('selectCountry')}
                                            name="country"
                                            isSearchable
                                            closeMenuOnSelect={true}
                                            value={formData.current_address?.country ? {
                                                value: formData.current_address.country,
                                                label: countriesData?.data?.find(c => String(c.id) === String(formData.current_address.country))?.name || formData.current_address.country
                                            } : null}
                                            onChange={(option) => {
                                                handleChange("current_address.country", option?.value || "");
                                                handleChange("current_address.country_name", option?.label || "");
                                                handleChange("current_address.state", "");
                                                handleChange("current_address.state_name", "");
                                            }}
                                            options={countriesData?.data?.map(country => ({ value: String(country.id), label: country.name })) || []}
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="address_1">{t('customerDetailTab.address1')}</Label>
                                        <Input
                                            id="address_1"
                                            type="text"
                                            value={formData.current_address?.address_1}
                                            onChange={(e) => handleChange("current_address.address_1", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterAddress1')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="address_2">{t('customerDetailTab.address2')}</Label>
                                        <Input
                                            id="address_2"
                                            type="text"
                                            value={formData.current_address?.address_2}
                                            onChange={(e) => handleChange("current_address.address_2", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterAddress2')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="4">
                                    <FormGroup>
                                        <Label for="zip_code">{t('customerDetailTab.zipCode')}</Label>
                                        <Input
                                            id="zip_code"
                                            type="text"
                                            value={formData.current_address?.zip_code}
                                            onChange={(e) => handleChange("current_address.zip_code", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterZIP')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup>
                                        <Label for="city">{t('customerDetailTab.city')}</Label>
                                        <Input
                                            id="city"
                                            type="text"
                                            value={formData.current_address?.city}
                                            onChange={(e) => handleChange("current_address.city", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterCity')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup>
                                        <Label for="county">{t('customerDetailTab.county')}</Label>
                                        <Input
                                            id="county"
                                            type="text"
                                            value={formData.current_address?.county}
                                            onChange={(e) => handleChange("current_address.county", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterCounty')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="state">{t('customerDetailTab.state')}</Label>
                                        <StatesInput
                                            name="current_address.state"
                                            value={formData.current_address?.state ? { id: formData.current_address.state } : null}
                                            onChange={(stateObject) => {
                                                handleChange("current_address.state", stateObject ? String(stateObject.id) : "");
                                                handleChange("current_address.state_name", stateObject ? stateObject.abbreviation : "");
                                            }}
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            placeholder={!formData.current_address?.country ? t('selectCountryFirst') :
                                                !currentAddrHasStates ? t('noStatesForCountry') : t('selectState')}
                                            countrySelected={!!formData.current_address?.country}
                                            isDisabled={!currentAddrHasStates}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label for="years_at">Years At</Label>
                                        <Input
                                            id="years_at"
                                            type="number"
                                            min="0"
                                            value={formData.current_address?.years_at}
                                            onChange={(e) => handleChange("current_address.years_at", e.target.value)}
                                            className="form-control"
                                            placeholder="0"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label for="months_at">Months At</Label>
                                        <Input
                                            id="months_at"
                                            type="number"
                                            min="0"
                                            max="11"
                                            value={formData.current_address?.months_at}
                                            onChange={(e) => handleChange("current_address.months_at", e.target.value)}
                                            className="form-control"
                                            placeholder="0"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="lien_holder">Lien Holder</Label>
                                        <Input
                                            id="lien_holder"
                                            type="text"
                                            value={formData.current_address?.lien_holder}
                                            onChange={(e) => handleChange("current_address.lien_holder", e.target.value)}
                                            className="form-control"
                                            placeholder="Enter Lien Holder"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="lien_holder_phone">Lien Holder Phone</Label>
                                        <Input
                                            id="lien_holder_phone"
                                            type="text"
                                            value={formData.current_address?.lien_holder_phone}
                                            onChange={(e) => handleChange("current_address.lien_holder_phone", formatPhoneNumber(e.target.value))}
                                            className="form-control"
                                            placeholder="Enter Lien Holder Phone"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="residence_payment">Residence Pmt</Label>
                                        <Input
                                            id="residence_payment"
                                            type="text"
                                            value={formData.current_address?.residence_payment}
                                            onChange={(e) => {
                                                const raw = e.target.value.replace(/[^\d.]/g, "");
                                                handleChange("current_address.residence_payment", raw);
                                            }}
                                            onBlur={() =>
                                                handleChange(
                                                    "current_address.residence_payment",
                                                    formatPriceWithDollar(formData.current_address?.residence_payment)
                                                )
                                            }
                                            onFocus={() => {
                                                const raw = String(formData.current_address?.residence_payment || "").replace(/[^\d.]/g, "");
                                                handleChange("current_address.residence_payment", raw);
                                            }}
                                            className="form-control"
                                            placeholder="$0.00"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="residence_type">Residence Type</Label>
                                        <Input
                                            id="residence_type"
                                            type="text"
                                            value={formData.current_address?.residence_type}
                                            onChange={(e) => handleChange("current_address.residence_type", e.target.value)}
                                            className="form-control"
                                            placeholder="Enter Residence Type"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="miles_from_lot">Miles From Lot</Label>
                                        <Input
                                            id="miles_from_lot"
                                            type="number"
                                            min="0"
                                            value={formData.current_address?.miles_from_lot}
                                            onChange={(e) => handleChange("current_address.miles_from_lot", e.target.value)}
                                            className="form-control"
                                            placeholder="0"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>
                <Col md="6">
                    <div className="card rounded-0 ">
                        <div className="card-header font-weight-bold">Previous Address</div>
                        <div className="card-body pt-0">
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_country">Country</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            placeholder={t('selectCountry')}
                                            name="prev_country"
                                            isSearchable
                                            closeMenuOnSelect={true}
                                            value={formData.previous_address?.prev_country ? {
                                                value: formData.previous_address.prev_country,
                                                label: countriesData?.data?.find(c => String(c.id) === String(formData.previous_address.prev_country))?.name || formData.previous_address.prev_country
                                            } : null}
                                            onChange={(option) => {
                                                handleChange("previous_address.prev_country", option?.value || "");
                                                handleChange("previous_address.prev_state", "");
                                            }}
                                            options={countriesData?.data?.map(country => ({ value: String(country.id), label: country.name })) || []}
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_address_1">Address 1</Label>
                                        <Input
                                            id="prev_address_1"
                                            type="text"
                                            value={formData.previous_address?.prev_address_1}
                                            onChange={(e) => handleChange("previous_address.prev_address_1", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterAddress1')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_address_2">Address 2</Label>
                                        <Input
                                            id="prev_address_2"
                                            type="text"
                                            value={formData.previous_address?.prev_address_2}
                                            onChange={(e) => handleChange("previous_address.prev_address_2", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterAddress2')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="4">
                                    <FormGroup>
                                        <Label for="prev_zip_code">ZIP Code</Label>
                                        <Input
                                            id="prev_zip_code"
                                            type="text"
                                            value={formData.previous_address?.prev_zip_code}
                                            onChange={(e) => handleChange("previous_address.prev_zip_code", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterZIP')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup>
                                        <Label for="prev_city">City</Label>
                                        <Input
                                            id="prev_city"
                                            type="text"
                                            value={formData.previous_address?.prev_city}
                                            onChange={(e) => handleChange("previous_address.prev_city", e.target.value)}
                                            className="form-control"
                                            placeholder={t('enterCity')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup>
                                        <Label for="prev_county">County</Label>
                                        <Input
                                            id="prev_county"
                                            type="text"
                                            value={formData.previous_address?.prev_county}
                                            onChange={(e) => handleChange("previous_address.prev_county", e.target.value)}
                                            className="form-control"
                                            placeholder={t('enterCounty')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_state">State</Label>
                                        <StatesInput
                                            name="previous_address.prev_state"
                                            value={formData.previous_address?.prev_state ? { id: formData.previous_address.prev_state } : null}
                                            onChange={(stateObject) => {
                                                handleChange("previous_address.prev_state", stateObject ? stateObject.id : "");
                                            }}
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            placeholder={!formData.previous_address?.prev_country ? t('selectCountryFirst') :
                                                !prevAddrHasStates ? t('noStatesForCountry') : t('selectState')}
                                            countrySelected={!!formData.previous_address?.prev_country}
                                            isDisabled={!prevAddrHasStates}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label for="prev_years_at">Years At</Label>
                                        <Input
                                            id="prev_years_at"
                                            type="number"
                                            min="0"
                                            value={formData.previous_address?.years_at}
                                            onChange={(e) => handleChange("previous_address.years_at", e.target.value)}
                                            className="form-control"
                                            placeholder="0"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label for="prev_months_at">Months At</Label>
                                        <Input
                                            id="prev_months_at"
                                            type="number"
                                            min="0"
                                            max="11"
                                            value={formData.previous_address?.months_at}
                                            onChange={(e) => handleChange("previous_address.months_at", e.target.value)}
                                            className="form-control"
                                            placeholder="0"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_lien_holder">Lien Holder</Label>
                                        <Input
                                            id="prev_lien_holder"
                                            type="text"
                                            value={formData.previous_address?.prev_lien_holder}
                                            onChange={(e) => handleChange("previous_address.prev_lien_holder", e.target.value)}
                                            className="form-control"
                                            placeholder="Enter Lien Holder"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_lien_holder_phone">Lien Holder Phone</Label>
                                        <Input
                                            id="prev_lien_holder_phone"
                                            type="text"
                                            value={formData.previous_address?.prev_lien_holder_phone}
                                            onChange={(e) => handleChange("previous_address.prev_lien_holder_phone", formatPhoneNumber(e.target.value))}
                                            className="form-control"
                                            placeholder="Enter Lien Holder Phone"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_residence_payment">Residence Pmt</Label>
                                        <Input
                                            id="prev_residence_payment"
                                            type="text"
                                            value={formData.previous_address?.prev_residence_payment}
                                            onChange={(e) => {
                                                const raw = e.target.value.replace(/[^\d.]/g, "");
                                                handleChange("previous_address.prev_residence_payment", raw);
                                            }}
                                            onBlur={() =>
                                                handleChange(
                                                    "previous_address.prev_residence_payment",
                                                    formatPriceWithDollar(formData.previous_address?.prev_residence_payment)
                                                )
                                            }
                                            onFocus={() => {
                                                const raw = String(formData.previous_address?.prev_residence_payment || "").replace(/[^\d.]/g, "");
                                                handleChange("previous_address.prev_residence_payment", raw);
                                            }}
                                            className="form-control"
                                            placeholder="$0.00"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_residence_type">Residence Type</Label>
                                        <Input
                                            id="prev_residence_type"
                                            type="text"
                                            value={formData.previous_address?.prev_residence_type}
                                            onChange={(e) => handleChange("previous_address.prev_residence_type", e.target.value)}
                                            className="form-control"
                                            placeholder="Enter Residence Type"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row className="">
                <Col md="6">
                    <div className="card rounded-0 ">
                        <div className="card-header font-weight-bold">{t('customerDetailTab.currentEmployment')}</div>
                        <div className="card-body pt-0">
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for='employment_employer'>{t('customerDetailTab.employer')}</Label>
                                        <Input
                                            id="employment_employer"
                                            type="text"
                                            value={formData.current_employment?.employment_employer}
                                            onChange={(e) => handleChange("current_employment.employment_employer", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterEmployer')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for='employment_supervisor_phone'>{t('customerDetailTab.supervisorPhone')}</Label>
                                        <Input
                                            id="employment_supervisor_phone"
                                            type="text"
                                            value={formData.current_employment?.employment_supervisor_phone}
                                            onChange={(e) => handleChange("current_employment.employment_supervisor_phone", formatPhoneNumber(e.target.value))}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterSupervisorPhone')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for='employment_position'>{t('customerDetailTab.position')}</Label>
                                        <Input
                                            id="employment_position"
                                            type="text"
                                            value={formData.current_employment?.employment_position}
                                            onChange={(e) => handleChange("current_employment.employment_position", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterPosition')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for='employment_type'>{t('customerDetailTab.employmentType')}</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            placeholder={t('customerDetailTab.selectType')}
                                            name="employment_type"
                                            isSearchable={false}
                                            closeMenuOnSelect={true}
                                            value={formData.current_employment?.employment_type ? {
                                                value: formData.current_employment.employment_type,
                                                label: formData.current_employment.employment_type === "Full-Time" ? t('customerDetailTab.fullTime') :
                                                    formData.current_employment.employment_type === "Part-Time" ? t('customerDetailTab.partTime') :
                                                        formData.current_employment.employment_type === "Self-Employed" ? t('customerDetailTab.selfEmployed') :
                                                            formData.current_employment.employment_type === "Contract" ? t('customerDetailTab.contract') :
                                                                formData.current_employment.employment_type === "Unemployed" ? t('customerDetailTab.unemployed') :
                                                                    formData.current_employment.employment_type
                                            } : null}
                                            onChange={(option) => {
                                                handleChange("current_employment.employment_type", option?.value || "");
                                            }}
                                            options={[
                                                { value: "Full-Time", label: t('customerDetailTab.fullTime') },
                                                { value: "Part-Time", label: t('customerDetailTab.partTime') },
                                                { value: "Self-Employed", label: t('customerDetailTab.selfEmployed') },
                                                { value: "Contract", label: t('customerDetailTab.contract') },
                                                { value: "Unemployed", label: t('customerDetailTab.unemployed') }
                                            ]}
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for='employment_income'>{t('customerDetailTab.income')}</Label>
                                        <Input
                                            id="employment_income"
                                            type="text"
                                            value={formData.current_employment?.employment_income}
                                            onChange={(e) => {
                                                const raw = e.target.value.replace(/[^\d.]/g, "");
                                                handleChange("current_employment.employment_income", raw);
                                            }}
                                            onBlur={() =>
                                                handleChange(
                                                    "current_employment.employment_income",
                                                    formatPriceWithDollar(formData.current_employment?.employment_income)
                                                )
                                            }
                                            onFocus={() => {
                                                const raw = String(formData.current_employment?.employment_income || "").replace(/[^\d.]/g, "");
                                                handleChange("current_employment.employment_income", raw);
                                            }}
                                            className="form-control"
                                            placeholder="$0.00"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for='employment_pay_frequency'>{t('customerDetailTab.payFrequency')}</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            placeholder={t('customerDetailTab.selectFrequency')}
                                            name="employment_pay_frequency"
                                            isSearchable={false}
                                            closeMenuOnSelect={true}
                                            value={formData.current_employment?.employment_pay_frequency ? {
                                                value: formData.current_employment.employment_pay_frequency,
                                                label: formData.current_employment.employment_pay_frequency === "Weekly" ? t('customerDetailTab.weekly') :
                                                    formData.current_employment.employment_pay_frequency === "Bi-Weekly" ? t('customerDetailTab.biWeekly') :
                                                        formData.current_employment.employment_pay_frequency === "Semi-Monthly" ? t('customerDetailTab.semiMonthly') :
                                                            formData.current_employment.employment_pay_frequency === "Monthly" ? t('customerDetailTab.monthly') :
                                                                formData.current_employment.employment_pay_frequency === "Annually" ? t('customerDetailTab.annually') :
                                                                    formData.current_employment.employment_pay_frequency
                                            } : null}
                                            onChange={(option) => handleChange("current_employment.employment_pay_frequency", option?.value || "")}
                                            options={[
                                                { value: "Weekly", label: t('customerDetailTab.weekly') },
                                                { value: "Bi-Weekly", label: t('customerDetailTab.biWeekly') },
                                                { value: "Semi-Monthly", label: t('customerDetailTab.semiMonthly') },
                                                { value: "Monthly", label: t('customerDetailTab.monthly') },
                                                { value: "Annually", label: t('customerDetailTab.annually') }
                                            ]}
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="3">
                                    <FormGroup>
                                        <Label for='employment_years_at'>{t('customerDetailTab.yearsAt')}</Label>
                                        <Input
                                            id="employment_years_at"
                                            type="number"
                                            min="0"
                                            value={formData.current_employment?.employment_years_at}
                                            onChange={(e) => handleChange("current_employment.employment_years_at", e.target.value)}
                                            className="form-control"
                                            placeholder="0"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label for='employment_months_at'>{t('customerDetailTab.monthsAt')}</Label>
                                        <Input
                                            id="employment_months_at"
                                            type="number"
                                            min="0"
                                            max="11"
                                            value={formData.current_employment?.employment_months_at}
                                            onChange={(e) => handleChange("current_employment.employment_months_at", e.target.value)}
                                            className="form-control"
                                            placeholder="0"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="employment_address_1">Address 1</Label>
                                        <Input
                                            id="employment_address_1"
                                            type="text"
                                            value={formData.current_employment?.employment_address_1}
                                            onChange={(e) => handleChange("current_employment.employment_address_1", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterAddress1')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="employment_address_2">Address 2</Label>
                                        <Input
                                            id="employment_address_2"
                                            type="text"
                                            value={formData.current_employment?.employment_address_2}
                                            onChange={(e) => handleChange("current_employment.employment_address_2", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterAddress2')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="4">
                                    <FormGroup>
                                        <Label for="employment_zip_code">ZIP Code</Label>
                                        <Input
                                            id="employment_zip_code"
                                            type="text"
                                            value={formData.current_employment?.employment_zip_code}
                                            onChange={(e) => handleChange("current_employment.employment_zip_code", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterZIP')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup>
                                        <Label for="employment_city">City</Label>
                                        <Input
                                            id="employment_city"
                                            type="text"
                                            value={formData.current_employment?.employment_city}
                                            onChange={(e) => handleChange("current_employment.employment_city", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterCity')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup>
                                        <Label for="employment_county">County</Label>
                                        <Input
                                            id="employment_county"
                                            type="text"
                                            value={formData.current_employment?.employment_county}
                                            onChange={(e) => handleChange("current_employment.employment_county", e.target.value)}
                                            className="form-control"
                                            placeholder={t('enterCounty')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="employment_state">State</Label>
                                        <StatesInput
                                            name="current_employment.employment_state"
                                            value={formData.current_employment?.employment_state ? { id: formData.current_employment.employment_state } : null}
                                            onChange={(stateObject) => {
                                                handleChange("current_employment.employment_state", stateObject ? String(stateObject.id) : "");
                                            }}
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            placeholder={!formData.current_employment?.employment_country ? t('selectCountryFirst') :
                                                !employmentHasStates ? t('noStatesForCountry') : t('selectState')}
                                            countrySelected={!!formData.current_employment?.employment_country}
                                            isDisabled={!employmentHasStates}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="employment_country">Country</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            placeholder={t('selectCountry')}
                                            name="employment_country"
                                            isSearchable
                                            closeMenuOnSelect={true}
                                            value={formData.current_employment?.employment_country ? {
                                                value: formData.current_employment.employment_country,
                                                label: countriesData?.data?.find(c => String(c.id) === String(formData.current_employment.employment_country))?.name || formData.current_employment.employment_country
                                            } : null}
                                            onChange={(option) => {
                                                handleChange("current_employment.employment_country", option?.value || "");
                                                handleChange("current_employment.employment_state", "");
                                            }}
                                            options={countriesData?.data?.map(country => ({ value: String(country.id), label: country.name })) || []}
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="12">
                                    <FormGroup>
                                        <Label for="employment_email">Email</Label>
                                        <Input
                                            id="employment_email"
                                            type="email"
                                            value={formData.current_employment?.employment_email}
                                            onChange={(e) => handleChange("current_employment.employment_email", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterEmail')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>
                <Col md="6">
                    <div className="card rounded-0 ">
                        <div className="card-header font-weight-bold">{t('customerDetailTab.previousEmployment')}</div>
                        <div className="card-body pt-0">
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_employment_employer">Employer</Label>
                                        <Input
                                            id="prev_employment_employer"
                                            type="text"
                                            value={formData.prev_employment_employer}
                                            onChange={(e) => handleChange("prev_employment_employer", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterEmployer')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_employment_supervisor_phone">Supervisor Phone</Label>
                                        <Input
                                            id="prev_employment_supervisor_phone"
                                            type="text"
                                            value={formData.prev_employment_supervisor_phone}
                                            onChange={(e) => handleChange("prev_employment_supervisor_phone", formatPhoneNumber(e.target.value))}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterSupervisorPhone')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_employment_position">Position</Label>
                                        <Input
                                            id="prev_employment_position"
                                            type="text"
                                            value={formData.prev_employment_position}
                                            onChange={(e) => handleChange("prev_employment_position", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterPosition')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_employment_type">Employment Type</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            placeholder={t('customerDetailTab.selectType')}
                                            name="prev_employment_type"
                                            isSearchable={false}
                                            closeMenuOnSelect={true}
                                            value={formData.prev_employment_type ? {
                                                value: formData.prev_employment_type,
                                                label: formData.prev_employment_type === "Full-Time" ? t('customerDetailTab.fullTime') :
                                                    formData.prev_employment_type === "Part-Time" ? t('customerDetailTab.partTime') :
                                                        formData.prev_employment_type === "Self-Employed" ? t('customerDetailTab.selfEmployed') :
                                                            formData.prev_employment_type === "Contract" ? t('customerDetailTab.contract') :
                                                                formData.prev_employment_type === "Unemployed" ? t('customerDetailTab.unemployed') :
                                                                    formData.prev_employment_type
                                            } : null}
                                            onChange={(option) => handleChange("prev_employment_type", option?.value || "")}
                                            options={[
                                                { value: "Full-Time", label: t('customerDetailTab.fullTime') },
                                                { value: "Part-Time", label: t('customerDetailTab.partTime') },
                                                { value: "Self-Employed", label: t('customerDetailTab.selfEmployed') },
                                                { value: "Contract", label: t('customerDetailTab.contract') },
                                                { value: "Unemployed", label: t('customerDetailTab.unemployed') }
                                            ]}
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_employment_income">Income</Label>
                                        <Input
                                            id="prev_employment_income"
                                            type="text"
                                            value={formData.prev_employment_income}
                                            onChange={(e) => {
                                                const raw = e.target.value.replace(/[^\d.]/g, "");
                                                handleChange("prev_employment_income", raw);
                                            }}
                                            onBlur={() =>
                                                handleChange(
                                                    "prev_employment_income",
                                                    formatPriceWithDollar(formData.prev_employment_income)
                                                )
                                            }
                                            onFocus={() => {
                                                const raw = String(formData.prev_employment_income || "").replace(/[^\d.]/g, "");
                                                handleChange("prev_employment_income", raw);
                                            }}
                                            className="form-control"
                                            placeholder="$0.00"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_employment_pay_frequency">Pay Frequency</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            placeholder={t('customerDetailTab.selectFrequency')}
                                            name="prev_employment_pay_frequency"
                                            isSearchable={false}
                                            closeMenuOnSelect={true}
                                            value={formData.prev_employment_pay_frequency ? {
                                                value: formData.prev_employment_pay_frequency,
                                                label: formData.prev_employment_pay_frequency === "Weekly" ? t('customerDetailTab.weekly') :
                                                    formData.prev_employment_pay_frequency === "Bi-Weekly" ? t('customerDetailTab.biWeekly') :
                                                        formData.prev_employment_pay_frequency === "Semi-Monthly" ? t('customerDetailTab.semiMonthly') :
                                                            formData.prev_employment_pay_frequency === "Monthly" ? t('customerDetailTab.monthly') :
                                                                formData.prev_employment_pay_frequency === "Annually" ? t('customerDetailTab.annually') :
                                                                    formData.prev_employment_pay_frequency
                                            } : null}
                                            onChange={(option) => handleChange("prev_employment_pay_frequency", option?.value || "")}
                                            options={[
                                                { value: "Weekly", label: t('customerDetailTab.weekly') },
                                                { value: "Bi-Weekly", label: t('customerDetailTab.biWeekly') },
                                                { value: "Semi-Monthly", label: t('customerDetailTab.semiMonthly') },
                                                { value: "Monthly", label: t('customerDetailTab.monthly') },
                                                { value: "Annually", label: t('customerDetailTab.annually') }
                                            ]}
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="3">
                                    <FormGroup>
                                        <Label for="prev_employment_years_at">Years At</Label>
                                        <Input
                                            id="prev_employment_years_at"
                                            type="number"
                                            min="0"
                                            value={formData.prev_employment_years_at}
                                            onChange={(e) => handleChange("prev_employment_years_at", e.target.value)}
                                            className="form-control"
                                            placeholder="0"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3">
                                    <FormGroup>
                                        <Label for="prev_employment_months_at">Months At</Label>
                                        <Input
                                            id="prev_employment_months_at"
                                            type="number"
                                            min="0"
                                            max="11"
                                            value={formData.prev_employment_months_at}
                                            onChange={(e) => handleChange("prev_employment_months_at", e.target.value)}
                                            className="form-control"
                                            placeholder="0"
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_employment_address_1">Address 1</Label>
                                        <Input
                                            id="prev_employment_address_1"
                                            type="text"
                                            value={formData.prev_employment_address_1}
                                            onChange={(e) => handleChange("prev_employment_address_1", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterAddress1')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_employment_address_2">Address 2</Label>
                                        <Input
                                            id="prev_employment_address_2"
                                            type="text"
                                            value={formData.prev_employment_address_2}
                                            onChange={(e) => handleChange("prev_employment_address_2", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterAddress2')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="4">
                                    <FormGroup>
                                        <Label for="prev_employment_zip_code">ZIP Code</Label>
                                        <Input
                                            id="prev_employment_zip_code"
                                            type="text"
                                            value={formData.prev_employment_zip_code}
                                            onChange={(e) => handleChange("prev_employment_zip_code", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterZIP')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup>
                                        <Label for="prev_employment_city">City</Label>
                                        <Input
                                            id="prev_employment_city"
                                            type="text"
                                            value={formData.prev_employment_city}
                                            onChange={(e) => handleChange("prev_employment_city", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterCity')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup>
                                        <Label for="prev_employment_county">County</Label>
                                        <Input
                                            id="prev_employment_county"
                                            type="text"
                                            value={formData.prev_employment_county}
                                            onChange={(e) => handleChange("prev_employment_county", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterCounty')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_employment_state">State</Label>
                                        <StatesInput
                                            name="prev_employment_state"
                                            value={formData.prev_employment_state ? { id: formData.prev_employment_state } : null}
                                            onChange={(stateObject) => {
                                                handleChange("prev_employment_state", stateObject ? String(stateObject.id) : "");
                                            }}
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            placeholder={!formData.prev_employment_country ? t('selectCountryFirst') :
                                                !prevEmploymentHasStates ? t('noStatesForCountry') : t('selectState')}
                                            countrySelected={!!formData.prev_employment_country}
                                            isDisabled={!prevEmploymentHasStates}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label for="prev_employment_country">Country</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            placeholder={t('selectCountry')}
                                            name="prev_employment_country"
                                            isSearchable
                                            closeMenuOnSelect={true}
                                            value={formData.prev_employment_country ? {
                                                value: formData.prev_employment_country,
                                                label: countriesData?.data?.find(c => String(c.id) === String(formData.prev_employment_country))?.name || formData.prev_employment_country
                                            } : null}
                                            onChange={(option) => {
                                                handleChange("prev_employment_country", option?.value || "");
                                                handleChange("prev_employment_state", "");
                                            }}
                                            options={countriesData?.data?.map(country => ({ value: String(country.id), label: country.name })) || []}
                                            isClearable
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="12">
                                    <FormGroup>
                                        <Label for="prev_employment_email">Email</Label>
                                        <Input
                                            id="prev_employment_email"
                                            type="email"
                                            value={formData.prev_employment_email}
                                            onChange={(e) => handleChange("prev_employment_email", e.target.value)}
                                            className="form-control"
                                            placeholder={t('customerDetailTab.enterEmail')}
                                            style={inputStyle14}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>
            </Row>
            </div>
        </div>
    );
});

CustomerDetailTab.displayName = 'CustomerDetailTab';

export default CustomerDetailTab;
